import { useLayoutEffect, useRef } from "react";

interface VisibleReceipt {
  threadId?: string;
  enabled: boolean;
  revision: number;
  active: boolean;
  acknowledge: (revision: number) => Promise<void>;
}

export function useVisibleChatReadReceipt({
  threadId,
  enabled,
  revision,
  active,
  acknowledge: acknowledgeRevision,
}: VisibleReceipt) {
  const latest = useRef<VisibleReceipt>({
    threadId,
    enabled,
    revision,
    active,
    acknowledge: acknowledgeRevision,
  });
  const request = useRef<() => void>();
  const inFlight = useRef(false);

  useLayoutEffect(() => {
    latest.current = {
      threadId,
      enabled,
      revision,
      active,
      acknowledge: acknowledgeRevision,
    };
    request.current?.();
  }, [threadId, enabled, revision, active, acknowledgeRevision]);

  useLayoutEffect(() => {
    if (!threadId) return;
    let disposed = false;
    let attempted: VisibleReceipt | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let frame: number | undefined;
    const cancel = () => {
      if (timer !== undefined) clearTimeout(timer);
      if (frame !== undefined) cancelAnimationFrame(frame);
      timer = undefined;
      frame = undefined;
    };
    const eligible = () =>
      !disposed &&
      latest.current.threadId === threadId &&
      latest.current.enabled &&
      document.visibilityState === "visible";
    const acknowledge = () => {
      frame = undefined;
      if (!eligible() || inFlight.current || attempted === latest.current)
        return;
      const receipt = latest.current;
      attempted = receipt;
      inFlight.current = true;
      void receipt
        .acknowledge(receipt.revision)
        .catch(() => undefined)
        .finally(() => {
          inFlight.current = false;
          request.current?.();
        });
    };
    const afterPaint = () => {
      timer = undefined;
      if (eligible()) frame = requestAnimationFrame(acknowledge);
    };
    const schedule = () => {
      if (!eligible()) {
        cancel();
        return;
      }
      if (inFlight.current || attempted === latest.current) return;
      if (!latest.current.active) {
        cancel();
        afterPaint();
      } else if (timer === undefined && frame === undefined) {
        timer = setTimeout(afterPaint, 500);
      }
    };
    const onVisibilityChange = () => {
      // A visible retry is allowed even if the controller's last request failed.
      if (document.visibilityState === "visible") attempted = undefined;
      schedule();
    };
    request.current = schedule;
    document.addEventListener("visibilitychange", onVisibilityChange);
    schedule();
    return () => {
      disposed = true;
      cancel();
      request.current = undefined;
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [threadId]);
}
